import React from 'react';
import {
  AgnesPopover,
  AgnesPopoverTrigger,
  AgnesPopoverContent,
  AgnesButton,
  AgnesDateRangePickerInput,
  AgnesSelect,
  AgnesSelectTrigger,
  AgnesSelectValue,
  AgnesSelectContent,
  AgnesSelectItem,
} from '@agnes/ui';
import { Filter, Trash2, X } from 'lucide-react';
import { RISK_LABEL, type HorarioTableFiltros, type JornadaFiltrosOpcoes } from '@/lib/jornada';
import { labelFiltro, protegerSelectDoFechamento } from '@/lib/filtros';

// estado interno do popover: um valor por campo (single-select). Vira lista/bool/data no submit.
type FiltroForm = {
  empregador: string;
  risco: string;
  ocupacao: string;
  cargo: string;
  categoria: string;
  tipoRegistro: string;
  regimePrevidenciario: string;
  tipoJornada: string;
  cargaHorariaSemanal: string;
  horarioNoturno: string;
  tempoParcial: string;
  versao: string;
  declaracao: string;
  envio: string[];
};

const VAZIO: FiltroForm = {
  empregador: '',
  risco: '',
  ocupacao: '',
  cargo: '',
  categoria: '',
  tipoRegistro: '',
  regimePrevidenciario: '',
  tipoJornada: '',
  cargaHorariaSemanal: '',
  horarioNoturno: '',
  tempoParcial: '',
  versao: '',
  declaracao: '',
  envio: [],
};

const TRIGGER_CLS =
  'w-full min-w-0 [&>[data-slot=select-value]]:min-w-0 [&>[data-slot=select-value]]:flex-1 [&>[data-slot=select-value]]:truncate [&>[data-slot=select-value]]:text-left';

const optsFromLabels = (labels: Record<string, string>) =>
  Object.entries(labels).map(([value, label]) => ({ value, label }));

const strOpts = (labels: string[]) => labels.map((v) => ({ value: v, label: v }));

const SIM_NAO = [
  { value: 'true', label: 'Sim' },
  { value: 'false', label: 'Não' },
];

function toFiltros(f: FiltroForm): HorarioTableFiltros {
  const one = (v: string) => (v ? [v] : []);
  const oneNum = (v: string) => (v ? [Number(v)] : []);
  const oneBool = (v: string) => (v === '' ? null : v === 'true');
  return {
    cnpjList: one(f.empregador),
    step: [],
    status: [],
    classification: [],
    risk: one(f.risco),
    occupationCode: one(f.ocupacao),
    cargo: one(f.cargo),
    categoria: oneNum(f.categoria),
    tipoRegistro: one(f.tipoRegistro),
    regimePrevidenciario: oneNum(f.regimePrevidenciario),
    tipoJornada: oneNum(f.tipoJornada),
    cargaHorariaSemanal: oneNum(f.cargaHorariaSemanal),
    horarioNoturno: oneBool(f.horarioNoturno),
    tempoParcial: oneNum(f.tempoParcial),
    versao: one(f.versao),
    declaracao: one(f.declaracao),
    envioInicio: f.envio[0] || null,
    envioFim: f.envio[f.envio.length - 1] || null,
  };
}

type Props = {
  opcoes: JornadaFiltrosOpcoes;
  empregadores?: { value: string; label: string }[];
  onApply: (f: HorarioTableFiltros) => void;
  count?: number;
};

type SelectFieldProps = {
  value: string;
  onValueChange: (v: string) => void;
  placeholder: string;
  options: { value: string; label: string }[];
  dataQa: string;
};
function SelectField({ value, onValueChange, placeholder, options, dataQa }: SelectFieldProps) {
  const selectedLabel = options.find((option) => option.value === value)?.label;

  return (
    <AgnesSelect value={value} onValueChange={onValueChange}>
      <AgnesSelectTrigger className={TRIGGER_CLS} dataQa={dataQa}>
        <AgnesSelectValue placeholder={placeholder} title={selectedLabel} />
      </AgnesSelectTrigger>
      <AgnesSelectContent
        position="popper"
        dataQa={`${dataQa}-lista`}
        className="w-[var(--radix-select-trigger-width)] max-w-[calc(100vw-24px)]"
      >
        {options.map((o) => (
          // o ItemText do DS é o último span do item (flex): sem min-w-0 ele não encolhe
          // e o truncate do label não aplica.
          <AgnesSelectItem
            key={o.value}
            value={o.value}
            dataQa={dataQa}
            className="[&>span:last-child]:min-w-0"
          >
            <span className="block w-full min-w-0 truncate" title={o.label}>
              {o.label}
            </span>
          </AgnesSelectItem>
        ))}
      </AgnesSelectContent>
    </AgnesSelect>
  );
}

export default function HorariosFilter({ opcoes, empregadores = [], onApply, count = 0 }: Props) {
  const [open, setOpen] = React.useState(false);
  const [f, setF] = React.useState<FiltroForm>(VAZIO);
  const set = (k: keyof FiltroForm) => (v: string) => setF((s) => ({ ...s, [k]: v }));

  const aplicar = () => {
    onApply(toFiltros(f));
    setOpen(false);
  };
  const limpar = () => {
    setF(VAZIO);
    onApply(toFiltros(VAZIO));
  };

  return (
    <AgnesPopover open={open} onOpenChange={setOpen}>
      <AgnesPopoverTrigger asChild>
        <AgnesButton
          variant="outlined"
          startIcon={<Filter className="h-4 w-4" data-qa="horarios-filtro-icone-filtro" />}
          data-qa="horarios-filtro-trigger"
          aria-label={count > 0 ? `Filtro, ${count} ativos` : 'Filtro'}
        >
          {labelFiltro('Filtro', count)}
        </AgnesButton>
      </AgnesPopoverTrigger>

      <AgnesPopoverContent
        align="end"
        className="w-80 max-w-[calc(100vw-24px)] p-0"
        dataQa="horarios-filtro-popover"
        onInteractOutside={protegerSelectDoFechamento}
      >
        <div
          data-qa="horarios-filtro-cabecalho"
          className="flex items-center justify-between border-b border-agnes-border px-4 py-3"
        >
          <span
            data-qa="horarios-filtro-titulo"
            className="text-base font-semibold text-agnes-text"
          >
            Filtro
          </span>
          <button
            type="button"
            data-qa="horarios-filtro-fechar"
            aria-label="Fechar"
            onClick={() => setOpen(false)}
            className="text-agnes-text-muted hover:text-agnes-text"
          >
            <X className="h-5 w-5" data-qa="horarios-filtro-fechar-icone" />
          </button>
        </div>

        {/* shrink-0: com muitos campos, sem isso o flex comprime os selects em vez de rolar */}
        <div
          data-qa="horarios-filtro-campos"
          className="flex min-w-0 max-h-[60vh] flex-col gap-3 overflow-y-auto px-4 py-4 [&>*]:shrink-0"
        >
          {empregadores.length > 0 && (
            <SelectField
              value={f.empregador}
              dataQa="horarios-filtro-campo-empregador"
              onValueChange={set('empregador')}
              placeholder="Empregador"
              options={empregadores}
            />
          )}
          <SelectField
            value={f.risco}
            dataQa="horarios-filtro-campo-risco"
            onValueChange={set('risco')}
            placeholder="Grau de risco"
            options={optsFromLabels(RISK_LABEL)}
          />
          <SelectField
            value={f.ocupacao}
            dataQa="horarios-filtro-campo-ocupacao"
            onValueChange={set('ocupacao')}
            placeholder="Ocupação"
            options={opcoes.ocupacao}
          />
          <SelectField
            value={f.cargo}
            dataQa="horarios-filtro-campo-cargo"
            onValueChange={set('cargo')}
            placeholder="Cargo"
            options={strOpts(opcoes.cargo)}
          />
          <SelectField
            value={f.categoria}
            dataQa="horarios-filtro-campo-categoria"
            onValueChange={set('categoria')}
            placeholder="Categoria"
            options={opcoes.categoria}
          />
          <SelectField
            value={f.tipoRegistro}
            dataQa="horarios-filtro-campo-tipoRegistro"
            onValueChange={set('tipoRegistro')}
            placeholder="Tipo de registro"
            options={strOpts(opcoes.tipoRegistro)}
          />
          <SelectField
            value={f.regimePrevidenciario}
            dataQa="horarios-filtro-campo-regimePrevidenciario"
            onValueChange={set('regimePrevidenciario')}
            placeholder="Regime previdenciário"
            options={opcoes.regimePrevidenciario}
          />
          <SelectField
            value={f.tipoJornada}
            dataQa="horarios-filtro-campo-tipoJornada"
            onValueChange={set('tipoJornada')}
            placeholder="Tipo de jornada"
            options={opcoes.tipoJornada}
          />
          <SelectField
            value={f.cargaHorariaSemanal}
            dataQa="horarios-filtro-campo-cargaHorariaSemanal"
            onValueChange={set('cargaHorariaSemanal')}
            placeholder="Carga horária semanal"
            options={strOpts(opcoes.cargaHorariaSemanal)}
          />
          <SelectField
            value={f.horarioNoturno}
            dataQa="horarios-filtro-campo-horarioNoturno"
            onValueChange={set('horarioNoturno')}
            placeholder="Horário noturno"
            options={SIM_NAO}
          />
          <SelectField
            value={f.tempoParcial}
            dataQa="horarios-filtro-campo-tempoParcial"
            onValueChange={set('tempoParcial')}
            placeholder="Tempo parcial"
            options={opcoes.tempoParcial}
          />
          <AgnesDateRangePickerInput
            value={f.envio}
            onChange={(dates) => setF((s) => ({ ...s, envio: dates }))}
            label="Envio do evento"
            dataQa="horarios-filtro-campo-envio"
          />
          <SelectField
            value={f.versao}
            dataQa="horarios-filtro-campo-versao"
            onValueChange={set('versao')}
            placeholder="Versão"
            options={strOpts(opcoes.versao)}
          />
          <SelectField
            value={f.declaracao}
            dataQa="horarios-filtro-campo-declaracao"
            onValueChange={set('declaracao')}
            placeholder="Declaração"
            options={strOpts(opcoes.declaracao)}
          />
        </div>

        <div
          data-qa="horarios-filtro-acoes"
          className="flex flex-col gap-2 border-t border-agnes-border px-4 py-3"
        >
          <AgnesButton
            variant="primary"
            onClick={aplicar}
            className="w-full"
            data-qa="horarios-filtro-aplicar"
          >
            Aplicar filtro
          </AgnesButton>
          <AgnesButton
            variant="outlined"
            onClick={limpar}
            data-qa="horarios-filtro-limpar"
            startIcon={<Trash2 className="h-4 w-4" data-qa="horarios-filtro-limpar-icone" />}
            className="w-full"
          >
            Limpar filtro
          </AgnesButton>
        </div>
      </AgnesPopoverContent>
    </AgnesPopover>
  );
}
